// Shared meta + small presentational pieces for the users settings page.
import { ShieldCheck, ShieldStar, Users as UsersIcon } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { formatDate } from '@/utils/datetime';

export const ROLE_META = {
  admin:   { labelKey: 'users.role.admin',   descKey: 'users.role.adminDesc',   Icon: ShieldStar,  badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  manager: { labelKey: 'users.role.manager', descKey: 'users.role.managerDesc', Icon: ShieldCheck, badge: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
  user:    { labelKey: 'users.role.user',    descKey: 'users.role.userDesc',    Icon: UsersIcon,   badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',      dot: 'bg-zinc-400' },
};

export function fmtDate(iso) {
  if (!iso) return '—';
  return formatDate(iso) || iso;
}

// Capability key → translated label (falls back to the raw key for unknown caps).
export function TRANS_CAP(key, t) {
  const k = `cap.${key}`;
  const label = t(k);
  return label === k ? key : label;
}

export function RoleBadge({ role }) {
  const { t } = useT();
  const meta = ROLE_META[role] || ROLE_META.user;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.badge}`}>
      <Icon size={11} weight="fill" />
      {t(meta.labelKey)}
    </span>
  );
}

export function StatusPill({ active, disabled, onClick, testid, title }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
        active
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {active ? t('users.active') : t('users.inactive')}
    </button>
  );
}

export function StatCard({ Icon, label, value, color, testid }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-5 flex items-center gap-4 hover:shadow-sm transition-shadow" data-testid={testid}>
      <span className={`flex items-center justify-center w-11 h-11 rounded-lg bg-gradient-to-br ${color} text-white shadow-sm shrink-0`}>
        <Icon size={20} weight="duotone" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 truncate">{label}</p>
        <p className="text-2xl font-bold tabular-nums text-zinc-900 leading-tight">{value}</p>
      </div>
    </div>
  );
}
